import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import ItemService from '../service/ItemService'

// Endless-scroll product list (GET /shop/public/items/feed): newest first, one page at a time.
// - never more than one request at a time; a filter change cancels the old request and ignores late answers
// - items are de-duplicated by itemId when a page is appended
// - the list is remembered per filter for 10 minutes, so Back from a product restores it and the scroll position
//   without asking the server again
const CACHE_MS = 10 * 60 * 1000
const cache = new Map()

const keyOf = ({ age = '0', gender = '3', categoryId = '', size = '' }) => [age, gender, categoryId, size].join('|')

const EMPTY = { items: [], nextCursor: null, total: null, status: 'loading', added: 0 }

// status: 'loading' (first page) · 'ready' (more to load) · 'loading-more' · 'error' · 'error-more' · 'end'
const useItemFeed = ({ age, gender, categoryId, size } = {}) => {
    const history = useHistory()
    const key = keyOf({ age, gender, categoryId, size })
    const [state, setState] = useState(EMPTY)
    const stateRef = useRef(state)
    stateRef.current = state
    const inFlight = useRef(false)
    const generation = useRef(0)
    const cancel = useRef(null)
    const pendingScroll = useRef(null)
    // where the shopper is, kept up to date while scrolling (reading it while the page unmounts is too late)
    const lastScroll = useRef(0)
    // the card the shopper opened and its distance from the top of the screen
    const anchor = useRef(null)

    useEffect(() => {
        const onScroll = () => { lastScroll.current = window.scrollY }
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', onScroll)
            if (cancel.current) cancel.current.cancel()
        }
    }, [])

    const fetchPage = useCallback((cursor) => {
        if (inFlight.current) return
        inFlight.current = true
        const mine = generation.current
        const source = axios.CancelToken.source()
        cancel.current = source
        setState(s => ({ ...s, status: cursor ? 'loading-more' : 'loading' }))
        ItemService.getItemFeed({ age, gender, categoryId, size }, cursor, { cancelToken: source.token })
            .then(res => {
                if (mine !== generation.current) return
                const data = res.data || {}
                setState(s => {
                    const base = cursor ? s.items : []
                    const seen = new Set(base.map(i => i.itemId))
                    const fresh = (data.items || []).filter(i => !seen.has(i.itemId))
                    return {
                        items: base.concat(fresh),
                        nextCursor: data.nextCursor || null,
                        total: cursor ? s.total : (data.total != null ? data.total : fresh.length),
                        status: data.nextCursor ? 'ready' : 'end',
                        added: fresh.length
                    }
                })
            })
            .catch(error => {
                if (axios.isCancel(error) || mine !== generation.current) return
                console.log(error)
                setState(s => ({ ...s, status: cursor ? 'error-more' : 'error' }))
            })
            .finally(() => {
                if (mine === generation.current) inFlight.current = false
            })
    }, [age, gender, categoryId, size])

    // new filters: drop the old request and start from the remembered list (on Back) or the first page.
    // A layout effect, so a restored list is drawn before the first paint (no skeleton flash).
    useLayoutEffect(() => {
        generation.current += 1
        if (cancel.current) cancel.current.cancel()
        inFlight.current = false
        const saved = cache.get(key)
        if (saved && Date.now() - saved.savedAt < CACHE_MS) {
            anchor.current = null
            const { scrollY, anchor: savedAnchor, savedAt, ...list } = saved
            // only Back restores the position; a filter change shows the list from its top
            pendingScroll.current = history.action === 'POP' && scrollY != null ? { scrollY, anchor: savedAnchor } : null
            setState({ ...list, status: list.nextCursor ? 'ready' : 'end', added: 0 })
        } else {
            anchor.current = null
            setState(EMPTY)
            fetchPage(null)
        }
        const leavingKey = key
        return () => {
            const s = stateRef.current
            if (s.items.length && s.status !== 'error') {
                cache.set(leavingKey, { items: s.items, nextCursor: s.nextCursor, total: s.total, scrollY: lastScroll.current, anchor: anchor.current, savedAt: Date.now() })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key])

    // once the restored list is in the DOM, put the opened card back where it was on screen (cards far off screen
    // aren't drawn and only have an estimated height, so a plain pixel position would drift); then once more on the
    // next frame, after the cards around it have been drawn at their real size
    useLayoutEffect(() => {
        const target = pendingScroll.current
        if (target == null || !state.items.length) return
        pendingScroll.current = null
        // jump, don't animate: the site scrolls smoothly (scroll-behavior: smooth), which would start from the top
        const root = document.documentElement
        const jump = (y) => {
            root.style.scrollBehavior = 'auto'
            window.scrollTo(0, y)
            root.style.scrollBehavior = ''
        }
        const place = () => {
            const card = target.anchor && document.querySelector(`[data-item-id="${target.anchor.itemId}"]`)
            if (card) jump(window.scrollY + card.getBoundingClientRect().top - target.anchor.top)
            else jump(target.scrollY)
        }
        jump(target.scrollY)
        place()
        // cards drawn around it may still change height: keep it in place for a moment, unless the shopper scrolls
        let frame = null
        let stopped = false
        const stop = () => {
            stopped = true
            if (frame) cancelAnimationFrame(frame)
            window.removeEventListener('wheel', stop)
            window.removeEventListener('touchstart', stop)
            window.removeEventListener('keydown', stop)
        }
        window.addEventListener('wheel', stop, { passive: true })
        window.addEventListener('touchstart', stop, { passive: true })
        window.addEventListener('keydown', stop)
        const until = performance.now() + 700
        const hold = () => {
            if (stopped) return
            place()
            if (performance.now() < until) frame = requestAnimationFrame(hold)
            else stop()
        }
        frame = requestAnimationFrame(hold)
    }, [state.items])

    const rememberAnchor = useCallback((itemId) => {
        const card = document.querySelector(`[data-item-id="${itemId}"]`)
        anchor.current = card ? { itemId, top: card.getBoundingClientRect().top } : null
        lastScroll.current = window.scrollY
    }, [])

    const loadMore = useCallback(() => {
        const s = stateRef.current
        if (s.status === 'ready' && s.nextCursor) fetchPage(s.nextCursor)
    }, [fetchPage])

    const retry = useCallback(() => {
        const s = stateRef.current
        if (s.status === 'error') fetchPage(null)
        else if (s.status === 'error-more') fetchPage(s.nextCursor)
    }, [fetchPage])

    return { ...state, loadMore, retry, rememberAnchor }
}

export default useItemFeed
